import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { ACCOUNTS_API_URL, AFES_API_URL, CONTRACTS_API_URL, INDEXES_API_URL, PPA_API_URL, SEARCH_API_URL } from '../../constants';
import { OfftakePricingPanel } from './OfftakePricingPanel';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

export type AdoptionSection = 'accounts' | 'afes' | 'indexes' | 'ppa';

interface AccountRow {
  id: string;
  code: string;
  name: string;
  documentKind: string;
  active: boolean;
  afeRequired: string;
}

interface AfeRow {
  id: string;
  number: string;
  name: string;
  kind: string;
  status: string;
  wellId?: string;
  operatingRegion: string;
  approvedAmount?: number;
}

interface IndexRow {
  code: string;
  name: string;
  productCode?: string;
  volumeProductCode?: string;
  publisherCode?: string;
  grain?: string;
  kind?: string;
}

interface ObserveRow {
  indexCode: string;
  periodId: string;
  settle: number;
}

interface QuoteRow {
  seriesCode: string;
  quoteAt: string;
  price: number;
  source: string;
  status: string;
}

interface SubscriptionRow {
  seriesCode: string;
  mode: string;
  status: string;
  cadence: string;
}

interface PublisherRow {
  code: string;
  name: string;
}

interface FormulaRow {
  id: string;
  name: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  product?: string | null;
  gravityMin?: number | null;
  gravityMax?: number | null;
  isDefault: boolean;
}

interface PpaRow {
  id: string;
  productionPeriod: string;
  booksPeriod: string;
  quantity: number;
  price: number;
  salesAmount: number;
  royaltyAmount: number;
  status: string;
  salesAccountCode?: string;
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    throw new Error((await res.text()) || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function AdoptionMastersPanel({
  section,
  authFetch,
  canWrite,
  periodId,
  onError
}: {
  section: AdoptionSection;
  authFetch: AuthFetch;
  canWrite: boolean;
  periodId: string;
  onError: (message: string) => void;
}): ReactElement {
  if (section === 'accounts') {
    return <AccountsRegister authFetch={authFetch} canWrite={canWrite} onError={onError} />;
  }
  if (section === 'afes') {
    return <AfeRegister authFetch={authFetch} canWrite={canWrite} onError={onError} />;
  }
  if (section === 'indexes') {
    return <IndexRegister authFetch={authFetch} canWrite={canWrite} periodId={periodId} onError={onError} />;
  }
  return <PpaRegister authFetch={authFetch} canWrite={canWrite} periodId={periodId} onError={onError} />;
}

function AccountsRegister({
  authFetch,
  canWrite,
  onError
}: {
  authFetch: AuthFetch;
  canWrite: boolean;
  onError: (message: string) => void;
}): ReactElement {
  const [rows, setRows] = useState<AccountRow[]>([]);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('Sales');
  const [afeRequired, setAfeRequired] = useState('No');

  const load = useCallback(async () => {
    setRows(await readJson<AccountRow[]>(await authFetch(ACCOUNTS_API_URL)));
  }, [authFetch]);

  useEffect(() => {
    void load().catch((e) => onError(e instanceof Error ? e.message : 'Could not load accounts'));
  }, [load, onError]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    onError('');
    try {
      await readJson(
        await authFetch(ACCOUNTS_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, name, documentKind: kind, afeRequired })
        })
      );
      setCode('');
      setName('');
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not create account');
    }
  };

  return (
    <section className="card" data-testid="accounts-register">
      <h2>Chart of accounts</h2>
      <p>
        Naturals on the issued document. The well is the collector. AFE is an authorization, not a cost center.
      </p>
      <table className="requests-grid">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Document</th>
            <th>AFE</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.code}</td>
              <td>{row.name}</td>
              <td>{row.documentKind}</td>
              <td>{row.afeRequired}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canWrite && (
        <form className="grid fields-2" onSubmit={(e) => void onCreate(e)} data-testid="account-create-form">
          <label>
            Code
            <input value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
          <label>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Document
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option>Sales</option>
              <option>Royalty</option>
              <option>Purchase</option>
              <option>JIB</option>
            </select>
          </label>
          <label>
            AFE required
            <select value={afeRequired} onChange={(e) => setAfeRequired(e.target.value)}>
              <option>No</option>
              <option>Optional</option>
              <option>Yes</option>
            </select>
          </label>
          <button type="submit">Add account</button>
        </form>
      )}
    </section>
  );
}

function AfeRegister({
  authFetch,
  canWrite,
  onError
}: {
  authFetch: AuthFetch;
  canWrite: boolean;
  onError: (message: string) => void;
}): ReactElement {
  const [rows, setRows] = useState<AfeRow[]>([]);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('Workover');

  const load = useCallback(async () => {
    setRows(await readJson<AfeRow[]>(await authFetch(AFES_API_URL)));
  }, [authFetch]);

  useEffect(() => {
    void load().catch((e) => onError(e instanceof Error ? e.message : 'Could not load AFEs'));
  }, [load, onError]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    onError('');
    try {
      const wellSearch = await readJson<{ wells: { id: string }[] }>(
        await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent('42123400010000')}`)
      );
      await readJson(
        await authFetch(AFES_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            number,
            name,
            kind,
            status: 'Approved',
            wellId: wellSearch.wells[0]?.id,
            operatingRegion: 'Permian'
          })
        })
      );
      setNumber('');
      setName('');
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not create AFE');
    }
  };

  return (
    <section className="card" data-testid="afe-register">
      <h2>AFE authorizations</h2>
      <p>Pre-approved project numbers. Seed AFE-421234-WO is Approved on Demo San Andres 1. Not a cost-center tree.</p>
      <table className="requests-grid">
        <thead>
          <tr>
            <th>Number</th>
            <th>Name</th>
            <th>Kind</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.number}</td>
              <td>{row.name}</td>
              <td>{row.kind}</td>
              <td>
                <span className="status-pill">{row.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canWrite && (
        <form className="grid fields-2" onSubmit={(e) => void onCreate(e)} data-testid="afe-create-form">
          <label>
            Number
            <input value={number} onChange={(e) => setNumber(e.target.value)} />
          </label>
          <label>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Kind
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option>Capital</option>
              <option>Workover</option>
              <option>Expense</option>
              <option>Other</option>
            </select>
          </label>
          <button type="submit">Add Approved AFE</button>
        </form>
      )}
    </section>
  );
}

function IndexRegister({
  authFetch,
  canWrite,
  periodId,
  onError
}: {
  authFetch: AuthFetch;
  canWrite: boolean;
  periodId: string;
  onError: (message: string) => void;
}): ReactElement {
  const [indexes, setIndexes] = useState<IndexRow[]>([]);
  const [observations, setObservations] = useState<ObserveRow[]>([]);
  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);
  const [publishers, setPublishers] = useState<PublisherRow[]>([]);
  const [code, setCode] = useState('NYMEX-WTI');
  const [settle, setSettle] = useState('70');
  const [quoteDate, setQuoteDate] = useState(periodId ? `${periodId}-01` : '');
  const [quotePrice, setQuotePrice] = useState('70');
  const [importBody, setImportBody] = useState(
    '[{"quoteAt":"2026-09-01T00:00:00Z","price":70.00}]'
  );
  const [subStatus, setSubStatus] = useState('Ready');
  const [formulas, setFormulas] = useState<FormulaRow[]>([]);
  const [offtakeId, setOfftakeId] = useState('');

  const load = useCallback(async () => {
    const data = await readJson<{
      indexes: IndexRow[];
      observations: ObserveRow[];
      quotes?: QuoteRow[];
      subscriptions?: SubscriptionRow[];
      publishers?: PublisherRow[];
    }>(await authFetch(INDEXES_API_URL));
    setIndexes(data.indexes);
    setObservations(data.observations);
    setQuotes(data.quotes ?? []);
    setSubscriptions(data.subscriptions ?? []);
    setPublishers(data.publishers ?? []);
    const search = await readJson<{ contracts: { id: string }[] }>(
      await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent('T-20')}`)
    );
    const found = search.contracts[0]?.id ?? '';
    setOfftakeId(found);
    if (found) {
      setFormulas(await readJson<FormulaRow[]>(await authFetch(`${CONTRACTS_API_URL}/${found}/price-formulas`)));
    }
  }, [authFetch]);

  useEffect(() => {
    void load().catch((e) => onError(e instanceof Error ? e.message : 'Could not load indexes'));
  }, [load, onError]);

  const monthQuotes = quotes.filter((row) => row.quoteAt.slice(0, 7) === periodId && row.seriesCode === code);
  const subscription = subscriptions.find((row) => row.seriesCode === code);

  const onObserve = async (e: FormEvent) => {
    e.preventDefault();
    onError('');
    try {
      await readJson(
        await authFetch(`${INDEXES_API_URL}/${encodeURIComponent(code)}/observations/${encodeURIComponent(periodId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ settle: Number(settle) })
        })
      );
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save observation');
    }
  };

  const onQuote = async (e: FormEvent) => {
    e.preventDefault();
    onError('');
    try {
      await readJson(
        await authFetch(`${INDEXES_API_URL}/${encodeURIComponent(code)}/quotes`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quoteAt: `${quoteDate}T00:00:00Z`, price: Number(quotePrice), status: 'Final', source: 'Manual' })
        })
      );
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save quote');
    }
  };

  const onImport = async (e: FormEvent) => {
    e.preventDefault();
    onError('');
    try {
      const parsed = JSON.parse(importBody) as { quoteAt: string; price: number }[];
      await readJson(
        await authFetch(`${INDEXES_API_URL}/${encodeURIComponent(code)}/quotes/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: 'FileExport', batchId: 'ui-import', quotes: parsed })
        })
      );
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not import quotes');
    }
  };

  const onSubscribe = async (e: FormEvent) => {
    e.preventDefault();
    onError('');
    try {
      await readJson(
        await authFetch(`${INDEXES_API_URL}/${encodeURIComponent(code)}/subscription`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mode: subscription?.mode ?? 'FileExport',
            cadence: subscription?.cadence ?? 'Daily',
            status: subStatus
          })
        })
      );
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save subscription');
    }
  };

  return (
    <section className="card" data-testid="index-register">
      <h2>Market data — published quotes</h2>
      <p>
        Daily NYMEX / Argus / Platts quotes feed a 5-day or calendar-month average on the offtake. T-20 is 5-day
        NYMEX WTI (first five published days). This is an export or subscription port — not a live ticker.
      </p>
      {publishers.length > 0 && (
        <p data-testid="index-publishers">Publishers: {publishers.map((row) => row.code).join(', ')}</p>
      )}
      <table className="requests-grid" data-testid="price-formula-grid">
        <thead>
          <tr>
            <th>T-20 formula</th>
            <th>In force</th>
            <th>Picker</th>
          </tr>
        </thead>
        <tbody>
          {formulas.map((row) => (
            <tr key={row.id}>
              <td>{row.name}</td>
              <td>
                {row.effectiveFrom}
                {row.effectiveTo ? ` – ${row.effectiveTo}` : ' onward'}
              </td>
              <td>
                {row.isDefault
                  ? 'Default'
                  : row.gravityMin != null
                    ? `Gravity ${row.gravityMin}–${row.gravityMax}`
                    : row.product || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {offtakeId && (
        <OfftakePricingPanel
          contractId={offtakeId}
          authFetch={authFetch}
          defaultDate={periodId ? `${periodId}-01` : '2026-09-01'}
          onError={onError}
        />
      )}
      <table className="requests-grid" data-testid="index-series-grid">
        <thead>
          <tr>
            <th>Series</th>
            <th>Volume product</th>
            <th>Grade</th>
            <th>Publisher</th>
            <th>Grain</th>
            <th>Kind</th>
            <th>Name</th>
          </tr>
        </thead>
        <tbody>
          {indexes.map((row) => (
            <tr key={row.code}>
              <td>{row.code}</td>
              <td>{row.volumeProductCode || '—'}</td>
              <td>{row.productCode || '—'}</td>
              <td>{row.publisherCode || '—'}</td>
              <td>{row.grain || 'Monthly'}</td>
              <td>{row.kind || 'Posted'}</td>
              <td>{row.name}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="requests-grid" data-testid="index-quote-grid">
        <thead>
          <tr>
            <th>Series</th>
            <th>Published</th>
            <th>Price</th>
            <th>Source</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {monthQuotes.map((row) => (
            <tr key={`${row.seriesCode}-${row.quoteAt}`}>
              <td>{row.seriesCode}</td>
              <td>{row.quoteAt.slice(0, 10)}</td>
              <td>{row.price.toFixed(2)}</td>
              <td>{row.source}</td>
              <td>{row.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="requests-grid">
        <thead>
          <tr>
            <th>Monthly lock</th>
            <th>Period</th>
            <th>Settle</th>
          </tr>
        </thead>
        <tbody>
          {observations.map((row) => (
            <tr key={`${row.indexCode}-${row.periodId}`}>
              <td>{row.indexCode}</td>
              <td>{row.periodId}</td>
              <td>{row.settle.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canWrite && (
        <>
          <form className="grid fields-2" onSubmit={(e) => void onQuote(e)} data-testid="index-quote-form">
            <label>
              Series
              <select value={code} onChange={(e) => setCode(e.target.value)}>
                {indexes.map((row) => (
                  <option key={row.code} value={row.code}>
                    {row.code}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Published day
              <input type="date" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)} />
            </label>
            <label>
              Price
              <input value={quotePrice} onChange={(e) => setQuotePrice(e.target.value)} />
            </label>
            <button type="submit">Save daily quote</button>
          </form>
          <form className="grid" onSubmit={(e) => void onImport(e)} data-testid="index-import-form">
            <label>
              File export JSON
              <textarea value={importBody} onChange={(e) => setImportBody(e.target.value)} rows={3} />
            </label>
            <button type="submit">Import quotes</button>
          </form>
          <form className="grid" onSubmit={(e) => void onSubscribe(e)} data-testid="index-subscription-form">
            <label>
              Subscription for {code}
              <select value={subStatus} onChange={(e) => setSubStatus(e.target.value)}>
                <option value="Ready">Ready</option>
                <option value="Disabled">Disabled</option>
              </select>
            </label>
            <p>Mode {subscription?.mode ?? 'FileExport'} · {subscription?.cadence ?? 'Daily'}. No vendor URL is stored.</p>
            <button type="submit">Save subscription port</button>
          </form>
          <form className="grid" onSubmit={(e) => void onObserve(e)} data-testid="index-observe-form">
            <label>
              Monthly lock
              <select value={code} onChange={(e) => setCode(e.target.value)}>
                {indexes.map((row) => (
                  <option key={row.code} value={row.code}>
                    {row.code}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Settle for {periodId || 'selected month'}
              <input value={settle} onChange={(e) => setSettle(e.target.value)} />
            </label>
            <button type="submit" disabled={!periodId}>
              Save monthly observe
            </button>
          </form>
        </>
      )}
    </section>
  );
}

function PpaRegister({
  authFetch,
  canWrite,
  periodId,
  onError
}: {
  authFetch: AuthFetch;
  canWrite: boolean;
  periodId: string;
  onError: (message: string) => void;
}): ReactElement {
  const [rows, setRows] = useState<PpaRow[]>([]);
  const [quantity, setQuantity] = useState('50');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setRows(await readJson<PpaRow[]>(await authFetch(PPA_API_URL)));
  }, [authFetch]);

  useEffect(() => {
    void load().catch((e) => onError(e instanceof Error ? e.message : 'Could not load PPAs'));
  }, [load, onError]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    onError('');
    setBusy(true);
    try {
      const [wellSearch, contractSearch] = await Promise.all([
        readJson<{ wells: { id: string }[] }>(
          await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent('42123400010000')}`)
        ),
        readJson<{ contracts: { id: string }[] }>(await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent('T-01')}`))
      ]);
      const wellId = wellSearch.wells[0]?.id;
      const contractId = contractSearch.contracts[0]?.id;
      if (!wellId || !contractId) {
        throw new Error('Seed well or T-01 is missing.');
      }
      const created = await readJson<PpaRow>(
        await authFetch(PPA_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productionPeriod: '2026-07',
            booksPeriod: periodId,
            wellId,
            contractId,
            quantity: Number(quantity),
            price: 70
          })
        })
      );
      await readJson(
        await authFetch(`${PPA_API_URL}/${created.id}/issue`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accountCode: '4100' })
        })
      );
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not post PPA');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" data-testid="ppa-register">
      <h2>Prior-period adjustment</h2>
      <p>
        Production month 2026-07 is Hard-closed. A volume PPA books the delta into the Open month and leaves the
        original July documents untouched. Reverse/rebook is only for months that are still Open.
      </p>
      <table className="requests-grid">
        <thead>
          <tr>
            <th>Production</th>
            <th>Books</th>
            <th>Qty</th>
            <th>Sales</th>
            <th>Royalty</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={6}>No PPAs yet.</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id}>
                <td>{row.productionPeriod}</td>
                <td>{row.booksPeriod}</td>
                <td>{row.quantity}</td>
                <td>{row.salesAmount}</td>
                <td>{row.royaltyAmount}</td>
                <td>
                  <span className="status-pill">{row.status}</span>
                  {row.salesAccountCode ? ` · ${row.salesAccountCode}` : ''}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {canWrite && (
        <form className="grid fields-2" onSubmit={(e) => void onCreate(e)} data-testid="ppa-create-form">
          <label>
            Oil delta (bbl) into {periodId || 'Open month'}
            <input value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </label>
          <button type="submit" disabled={busy || !periodId}>
            {busy ? 'Posting…' : 'Issue +50 bbl PPA from Hard-closed July'}
          </button>
        </form>
      )}
    </section>
  );
}
